import { Injectable, computed, inject, signal } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { FormControl } from '@angular/forms';
import dayjs from 'dayjs';
import { RegisterUserFormService } from 'src/app/pages/register-user/services/register-user-form.service';
import { TranslationService } from 'src/app/services/translation.service';
import { GoogleTranslateService } from 'src/app/services/google-translate.service';
import { LocaleCode } from '../interfaces/calendar-register-user.interface';
import { closeAllCiSnackbar } from '@iurban-malaga/cicerone-ui';
import { BreakpointsService } from 'src/app/services/breakpoints.service';

@Injectable({
  providedIn: 'root'
})
export class DateFieldService {
  // Services
  private _registerUserFormService = inject(RegisterUserFormService);
  private _translationService = inject(TranslationService);
  private _googleTranslateService = inject(GoogleTranslateService);
  private _breakpointsService = inject(BreakpointsService);

  // State
  private _selectedDateRange = signal<DateRange<Date> | null>(null);
  public selectedDateRange = this._selectedDateRange.asReadonly();

  // Signals
  private _selectedLanguage = this._translationService.selectedLanguage;
  private _isMobile = this._breakpointsService.isMobile;

  // Computed
  public localeCode = computed(() => 
    this._googleTranslateService.cookieLangMapping(this._selectedLanguage())
  );

  // Constants
  public readonly minDate = new Date();

  /**
   * Process date range selection and update form controls
   */
  processDateRangeSelection(
    dateRange: DateRange<Date>,
    startDateControl: FormControl,
    endDateControl: FormControl
  ): void {
    this._selectedDateRange.set(dateRange);

    // Mark controls as touched and dirty
    this._markControlsAsInteracted(startDateControl, endDateControl);

    // Update start date
    this._updateStartDate(dateRange.start, startDateControl);
    
    // Update end date
    this._updateEndDate(dateRange.end, endDateControl);
  }

  /**
   * Format date for display
   */
  formatDateForDisplay(date: Date | null): string {
    if (!date) return '-';
    
    dayjs.locale(this._selectedLanguage());
    // return dayjs(date).format('ddd D MMM');
    return dayjs(date).format('dddd D MMMM');
  }

  /**
   * Get formatted date from current range
   */
  getFormattedDate(type: 'start' | 'end'): string {
    const range = this._selectedDateRange();
    if (!range) return '-';
    
    const date = type === 'start' ? range.start : range.end;
    return this.formatDateForDisplay(date);
  }

  /**
   * Validate date fields for mobile/desktop
   */
  validateDateFields(
    startDateControl: FormControl,
    endDateControl: FormControl,
    isNextButtonClicked: boolean
  ): boolean {
    if (this._isMobile()) {
      // Mobile: validate only when next button is clicked
      return isNextButtonClicked && (
        this._hasRequiredError(startDateControl) || 
        this._hasRequiredError(endDateControl)
      );
    } else {
      // Desktop: validate immediately when touched
      return this._hasRequiredError(startDateControl) || 
             this._hasRequiredError(endDateControl);
    }
  }

  /**
   * Mark form controls as touched for desktop validation
   */
  markControlsAsTouchedForDesktop(
    startDateControl: FormControl,
    endDateControl: FormControl,
    markAllAsTouched: boolean
  ): void {
    if (!this._isMobile() && markAllAsTouched) {
      this._markControlsAsInteracted(startDateControl, endDateControl);
    }
  }

  /**
   * Get first day of week based on locale
   */
  getFirstDayOfWeek(): number {
    return this.localeCode() === LocaleCode['EN_GB'] ? 0 : 1; // Sunday : Monday
  }

  /**
   * Reset service state
   */
  resetState(): void {
    this._selectedDateRange.set(null);
  }

  // Private methods
  private _markControlsAsInteracted(
    startDateControl: FormControl,
    endDateControl: FormControl
  ): void {
    startDateControl.markAsTouched();
    startDateControl.markAsDirty();
    endDateControl.markAsTouched();
    endDateControl.markAsDirty();
  }

  private _updateStartDate(startDate: Date | null, control: FormControl): void {
    if (startDate) {
      const formattedDate = this._formatDateString(startDate);
      control.setValue(formattedDate);
      this._registerUserFormService.setStartDate(startDate);
    } else {
      control.setValue('');
      this._registerUserFormService.setStartDate(new Date());
    }
  }

  private _updateEndDate(endDate: Date | null, control: FormControl): void {
    if (endDate) {
      const formattedDate = this._formatDateString(endDate);
      control.setValue(formattedDate);
      this._registerUserFormService.setEndDate(endDate);

      // Close snackbars for mobile UX when date range is complete
      if (this._isMobile()) {
        closeAllCiSnackbar();
      }
    } else {
      control.setValue(null);
      this._registerUserFormService.setEndDate(null);
    }
  }

  private _hasRequiredError(control: FormControl): boolean {
    return control.hasError('required') && (control.touched || control.dirty);
  }

  /**
   * Format date to dd/MM/yyyy string format
   */
  private _formatDateString(date: Date): string {
    return dayjs(date).format('DD/MM/YYYY');
  }
}
